import {
  collection, doc, getDoc, getDocs, query, where, limit,
} from 'firebase/firestore';

export const MEMBER_DOC_CATALOG = [
  { id: 'aadhar', label: 'Aadhar Card', icon: '🪪', keys: ['aadharCard', 'aadhar', 'aadhaar', 'aadhar_card', 'aadhaarCard'] },
  { id: 'id_card', label: 'ID Card', icon: '🪪', keys: ['idCard', 'id_card', 'governmentId', 'govId'] },
  { id: 'nda', label: 'NDA', icon: '📄', keys: ['nda', 'ndaDocument', 'nda_file', 'signedNda'] },
  { id: 'offer_letter', label: 'Job Offer Letter', icon: '📋', keys: ['jobOfferLetter', 'offerLetter', 'offer_letter', 'employmentLetter'] },
  { id: 'resume', label: 'Resume', icon: '📝', keys: ['resume', 'cv', 'resumeUrl'] },
  { id: 'pan', label: 'PAN Card', icon: '🪪', keys: ['panCard', 'pan', 'pan_card'] },
  { id: 'passport', label: 'Passport', icon: '🛂', keys: ['passport', 'passportCopy'] },
];

const ARRAY_KEYS = [
  'attachments', 'documents', 'onboardingDocuments', 'memberDocuments',
  'files', 'infoAttachments', 'submittedDocuments',
];

const CATALOG_KEYS = new Set(MEMBER_DOC_CATALOG.flatMap(c => c.keys));

function isUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
}

function fileNameFromUrl(url) {
  try {
    const base = new URL(url).pathname.split('/').pop() || 'document';
    return decodeURIComponent(base.split('?')[0]) || 'document';
  } catch {
    return 'document';
  }
}

function titleCase(value = '') {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, ch => ch.toUpperCase());
}

function catalogMetaForKey(key) {
  return MEMBER_DOC_CATALOG.find(cat => cat.keys.includes(key)) || null;
}

export function normalizeAttachment(raw, defaults = {}) {
  if (!raw) return null;

  if (typeof raw === 'string') {
    if (!isUrl(raw)) return null;
    return {
      url: raw.trim(),
      name: defaults.name || fileNameFromUrl(raw),
      label: defaults.label || 'Document',
      icon: defaults.icon || '📎',
      type: defaults.id || defaults.type || 'other',
      mimeType: null,
    };
  }

  if (typeof raw !== 'object') return null;

  const url = raw.url || raw.downloadUrl || raw.fileUrl || raw.href
    || raw.link || raw.storageUrl || raw.publicUrl;
  if (!url || !isUrl(url)) return null;

  const cat = catalogMetaForKey(raw.docType || raw.type || raw.kind || defaults.id || '');

  return {
    url: url.trim(),
    name: raw.name || raw.fileName || raw.filename || defaults.name || fileNameFromUrl(url),
    label: raw.label || raw.title || cat?.label || defaults.label || 'Document',
    icon: defaults.icon || raw.icon || cat?.icon || '📎',
    type: raw.type || raw.docType || raw.kind || defaults.id || cat?.id || 'other',
    mimeType: raw.mimeType || raw.contentType || null,
  };
}

export function extractAttachmentsFromRecord(record = {}) {
  const out = [];
  const seen = new Set();

  function add(raw, defaults = {}) {
    const item = normalizeAttachment(raw, defaults);
    if (!item || seen.has(item.url)) return;
    seen.add(item.url);
    out.push(item);
  }

  for (const cat of MEMBER_DOC_CATALOG) {
    for (const key of cat.keys) {
      if (record[key] != null) add(record[key], cat);
    }
  }

  for (const key of ARRAY_KEYS) {
    const arr = record[key];
    if (!Array.isArray(arr)) continue;
    arr.forEach(item => add(item));
  }

  Object.entries(record).forEach(([key, value]) => {
    if (ARRAY_KEYS.includes(key) || CATALOG_KEYS.has(key)) return;
    if (!/(?:url|link|file)$/i.test(key)) return;
    const cat = catalogMetaForKey(key.replace(/(?:Url|Link|File)$/i, ''));
    add(value, {
      label: cat?.label || titleCase(key.replace(/(?:Url|Link|File)$/i, '')),
      icon: cat?.icon,
      id: cat?.id,
    });
  });

  return out;
}

export function mergeAttachments(...lists) {
  const order = MEMBER_DOC_CATALOG.map(c => c.id);
  const seen = new Set();
  const out = [];

  for (const list of lists) {
    for (const item of list || []) {
      if (!item?.url || seen.has(item.url)) continue;
      seen.add(item.url);
      out.push(item);
    }
  }

  return out.sort((a, b) => {
    const ai = order.indexOf(a.type);
    const bi = order.indexOf(b.type);
    if (ai !== -1 || bi !== -1) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    return (a.label || '').localeCompare(b.label || '');
  });
}

export function isPdfAttachment(item) {
  const name = `${item?.name || ''} ${item?.url || ''}`.toLowerCase();
  return item?.mimeType === 'application/pdf' || name.includes('.pdf');
}

export function isImageAttachment(item) {
  const name = `${item?.name || ''} ${item?.url || ''}`.toLowerCase();
  if (item?.mimeType?.startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(name);
}

export async function fetchMemberAttachments(db, member) {
  const uid = member?.uid || member?.id;
  if (!db || !uid) return [];

  const buckets = [extractAttachmentsFromRecord(member)];

  try {
    const userSnap = await getDoc(doc(db, 'authorized_users', uid));
    if (userSnap.exists()) buckets.push(extractAttachmentsFromRecord(userSnap.data()));
  } catch (_) {}

  for (const sub of ['documents', 'member_documents', 'attachments']) {
    try {
      const snap = await getDocs(collection(db, 'authorized_users', uid, sub));
      snap.docs.forEach(d => {
        buckets.push(extractAttachmentsFromRecord({
          ...d.data(),
          label: d.data().label || d.data().title || titleCase(d.id),
        }));
      });
    } catch (_) {}
  }

  try {
    const reqSnap = await getDocs(query(
      collection(db, 'access_requests'),
      where('uid', '==', uid),
      limit(3),
    ));
    reqSnap.docs.forEach(d => buckets.push(extractAttachmentsFromRecord(d.data())));
  } catch (_) {}

  try {
    const legacySnap = await getDoc(doc(db, 'access_requests', uid));
    if (legacySnap.exists()) buckets.push(extractAttachmentsFromRecord(legacySnap.data()));
  } catch (_) {}

  if (member?.email) {
    try {
      const emailSnap = await getDocs(query(
        collection(db, 'access_requests'),
        where('email', '==', member.email),
        limit(3),
      ));
      emailSnap.docs.forEach(d => buckets.push(extractAttachmentsFromRecord(d.data())));
    } catch (_) {}
  }

  return mergeAttachments(...buckets);
}

export function attachmentFieldsFromRecord(record = {}) {
  const payload = {};
  for (const key of ARRAY_KEYS) {
    if (record[key] != null) payload[key] = record[key];
  }
  for (const cat of MEMBER_DOC_CATALOG) {
    for (const key of cat.keys) {
      if (record[key] != null) payload[key] = record[key];
    }
  }
  return payload;
}
